import { auth } from "../firebase/config"
import apiClient from "../api/apiClient"

// Local storage key for the current device's token
const CURRENT_TOKEN_KEY = "unity_smart_living_fcm_token"

// This function will be called from the mobile webview
export function updateNotificationToken(token: string): Promise<boolean> {
  // Store the token locally to identify this device
  localStorage.setItem(CURRENT_TOKEN_KEY, token)

  // Check if user is authenticated
  const currentUser = auth.currentUser
  if (!currentUser) {
    console.error("Cannot update notification token: User not authenticated")
    return Promise.resolve(false)
  }

  // Get the ID token for API authentication
  return currentUser
    .getIdToken()
    .then((idToken) => {
      // Set the authorization header
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${idToken}`

      // Send the token to our API
      return apiClient
        .post("/users/notification-token", { token })
        .then(() => {
          console.log("Notification token updated successfully")
          return true
        })
        .catch((error) => {
          console.error("Failed to update notification token:", error)
          return false
        })
    })
    .catch((error) => {
      console.error("Failed to get ID token:", error)
      return false
    })
}

// Function to remove a notification token
export function removeNotificationToken(token: string): Promise<boolean> {
  // If no token is provided, try to get it from local storage
  const tokenToRemove = token || localStorage.getItem(CURRENT_TOKEN_KEY)

  if (!tokenToRemove) {
    console.error("Cannot remove notification token: No token provided or stored")
    return Promise.resolve(false)
  }

  // Check if user is authenticated
  const currentUser = auth.currentUser
  if (!currentUser) {
    console.error("Cannot remove notification token: User not authenticated")
    return Promise.resolve(false)
  }

  // Get the ID token for API authentication
  return currentUser
    .getIdToken()
    .then((idToken) => {
      // Set the authorization header
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${idToken}`

      // Send the token removal request to our API
      return apiClient
        .delete("/users/notification-token", { data: { token: tokenToRemove } })
        .then(() => {
          console.log("Notification token removed successfully")
          // Clear the token from local storage if it matches the one we just removed
          if (localStorage.getItem(CURRENT_TOKEN_KEY) === tokenToRemove) {
            localStorage.removeItem(CURRENT_TOKEN_KEY)
          }
          return true
        })
        .catch((error) => {
          console.error("Failed to remove notification token:", error)
          return false
        })
    })
    .catch((error) => {
      console.error("Failed to get ID token:", error)
      return false
    })
}

// Function to get the current device's token
export function getCurrentDeviceToken(): string | null {
  return localStorage.getItem(CURRENT_TOKEN_KEY)
}

// Make the functions available globally for the mobile webview to call
declare global {
  interface Window {
    updateNotificationToken: (token: string) => Promise<boolean>
    removeNotificationToken: (token: string) => Promise<boolean>
    getCurrentDeviceToken: () => string | null
    navigateToRoute?: (route: string) => void
  }
}

// Expose the functions to the window object
if (typeof window !== "undefined") {
  window.updateNotificationToken = updateNotificationToken
  window.removeNotificationToken = removeNotificationToken
  window.getCurrentDeviceToken = getCurrentDeviceToken
}
